import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Loader2, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

export default function PaymentCompletePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [payment, setPayment] = useState(null);

  const paymentId = searchParams.get("payment_id") || localStorage.getItem("lh_pending_payment_id");

  useEffect(() => {
    if (!paymentId || !user) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const maxAttempts = 6;

    const checkStatus = async () => {
      try {
        const { data } = await api.get(`/payments/paynow/status/${paymentId}`);
        setPayment(data);
        if (data.status === "paid") {
          setStatus("paid");
          localStorage.removeItem("lh_pending_payment_id");
        } else if (data.status === "failed") {
          setStatus("failed");
          localStorage.removeItem("lh_pending_payment_id");
        } else {
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 3000);
          } else {
            setStatus("pending");
          }
        }
      } catch {
        setStatus("error");
      }
    };

    checkStatus();
  }, [paymentId, user]);

  const statusConfig = {
    checking: { icon: Loader2, color: "text-sky-500", bg: "bg-sky-50", title: "Verifying Payment...", desc: "We're confirming your payment with Paynow. Please wait.", iconClass: "animate-spin" },
    paid: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", title: "Payment Successful!", desc: `Your payment of $${payment?.amount?.toFixed(2) || "0.00"} has been confirmed. Your account has been credited.` },
    failed: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", title: "Payment Failed", desc: payment?.failure_reason || "Your payment could not be processed. Please try again or contact support." },
    pending: { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", title: "Payment Processing", desc: "Your payment is still being processed by Paynow. It may take a few minutes. Check your payments page for updates." },
    error: { icon: AlertTriangle, color: "text-slate-500", bg: "bg-slate-50", title: "Unable to Verify", desc: "We couldn't verify your payment status. Please check your payments page or contact support." },
  };

  const cfg = statusConfig[status];
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <Link to="/" className="flex items-center gap-2.5 mb-8">
        <div className="h-9 w-9 rounded-lg bg-sky-500 flex items-center justify-center">
          <Building2 className="h-5 w-5 text-white" />
        </div>
        <div>
          <span className="font-heading text-lg font-extrabold tracking-tight leading-none text-slate-900">LEGACY</span>
          <span className="font-heading text-[10px] font-bold tracking-[0.3em] block leading-none text-sky-600">HEIGHTS</span>
        </div>
      </Link>

      <Card className="w-full max-w-md border-slate-200 shadow-lg" data-testid="payment-complete-card">
        <CardContent className="pt-8 pb-8 text-center">
          <div className={`w-16 h-16 rounded-full ${cfg.bg} flex items-center justify-center mx-auto mb-5`}>
            <Icon className={`h-8 w-8 ${cfg.color} ${cfg.iconClass || ""}`} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2" data-testid="payment-status-title">{cfg.title}</h1>
          <p className="text-sm text-slate-500 leading-relaxed mb-6">{cfg.desc}</p>

          {payment && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left space-y-2">
              {payment.reference && <div className="flex justify-between text-sm"><span className="text-slate-500">Reference</span><span className="font-mono font-medium">{payment.reference}</span></div>}
              {payment.paynow_merchant_ref && <div className="flex justify-between text-sm"><span className="text-slate-500">Paynow Ref</span><span className="font-mono font-medium">{payment.paynow_merchant_ref}</span></div>}
              <div className="flex justify-between text-sm"><span className="text-slate-500">Amount</span><span className="font-bold">${payment.amount?.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Type</span><span className="capitalize">{payment.type}</span></div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button asChild className="w-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="go-to-payments-btn">
              <Link to="/tenant/payments">View My Payments</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/tenant">Back to Dashboard</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
